import DeleteIcon from "@mui/icons-material/Delete";
import {
    FormControl,
    FormControlLabel,
    Radio,
    RadioGroup,
} from "@mui/material";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
    BOTH_PRODUCTS_SMARTFORMS,
    CATALOGUE_ITEM,
    SEARCH_API_DELIMITER,
    SMART_FORM,
    SMARTFORMS_MODE,
} from "constants/generalConstants";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import GradientDivider from "styled/styledComponents/GradientDivider";
import { onCheckSmartForms } from "utils/productListUtils";

import FilterPanelAutoComplete from "../filterPanelAutocomplete";
import FilterPanel from "./filterPanel";

/**
 * React component for deciding which product list
 * filter panel to render: smartforms filter panel
 * vs product list filter panel. It also houses
 * filter elements reused across both filter panels
 * @component
 */

export const checkBoxStyle = { marginRight: 8, padding: "4px 0px" };

const ProductsFilterPanelDispatcher = ({
    isSmartForms,
    isProducts,
    searchParams,
    setSearchParams,
}) => {
    // Collect states and dispatchers
    // Temporarily remove GEN/GENIRE categories, which are not required for MVP
    const categories = useSelector((state) =>
        state.filterPanelLookups.CATEGORY_DISPLAY.filter(
            (cat) => cat.CATEGORY_DISPLAY_NAME[0] != "G"
        )
    );
    const filterLookups = useSelector((state) => state.filterPanelLookups);
    const category = useSelector((state) => state.filterPanelValues.category);
    const suppliers = useSelector((state) => state.filterPanelValues.suppliers);

    const [readSearchBarQuery, setReadSearchBarQuery] = useState();
    const [readItemNumberQuery, setReadItemNumberQuery] = useState();
    const [readSupplierNumberQuery, setReadSupplierNumberQuery] = useState();
    const unitOfMeasures = useSelector(
        (state) => state.filterPanelValues.unitOfMeasures
    );
    const currencies = useSelector(
        (state) => state.filterPanelValues.currencies
    );

    let initialSelectedRadioButtons = "";
    if (isSmartForms && isProducts)
        initialSelectedRadioButtons = BOTH_PRODUCTS_SMARTFORMS;
    else {
        if (isSmartForms) initialSelectedRadioButtons = SMART_FORM;
        else if (isProducts) initialSelectedRadioButtons = CATALOGUE_ITEM;
    }
    const [selectedRadioButtons, setSelectedRadioButtons] = useState(
        initialSelectedRadioButtons
    );
    // If search query supplied in URL, prepopulate the search bar with its value
    useEffect(() => {
        setReadSearchBarQuery(searchParams.get("search_query"));
        setReadItemNumberQuery(searchParams.get("item_number"));
        setReadSupplierNumberQuery(searchParams.get("supplier_number"));
    }, [searchParams]);

    const clearSearch = () => {
        const newParams = new URLSearchParams();
        isSmartForms && newParams.set("smartforms", SMARTFORMS_MODE);
        isProducts && newParams.set("products", SMARTFORMS_MODE);
        setSearchParams(newParams);
    };

    /**
     * Clears a specified param from product or smartform search query
     * @param {string} param - the search param to be deleted
     * @param {URLSearchParams} searchParams - url search params instantiated in `ProductResults`
     * @param {Function} setSearchParams - search params refresh from `useSearchParams` hook (react router)
     */
    const clearSearchBarQuery = (param, searchParams, setSearchParams) => {
        searchParams.delete(param);
        setSearchParams(searchParams);
    };

    /**
     * Called when a user updates supplier selection in products or smartforms filter panel
     * @param {object[]} value - suppliers selected by user in filter panel
     * @param {URLSearchParams} searchParams - url search params instantiated in `ProductResults`
     * @param {Function} setSearchParams - search params refresh from `useSearchParams` hook (react router)
     */
    const onSupplierSelect = (value, searchParams, setSearchParams) => {
        value.length > 0
            ? searchParams.set(
                  "supplier_name",
                  value.join(SEARCH_API_DELIMITER)
              )
            : searchParams.delete("supplier_name");
        setSearchParams(searchParams);
    };

    /**
     * Called when a user updates category selection in products or smartforms filter panel
     * @param {object[]} value - categories selected by user in filter panel
     * @param {URLSearchParams} searchParams - url search params instantiated in `ProductResults`
     * @param {Function} setSearchParams - search params refresh from `useSearchParams` hook (react router)
     */
    const onCategorySelect = (value, searchParams, setSearchParams) => {
        if (value.length > 0) {
            const purchasingCategoryIds = value.reduce(
                (acc, cat) => [...acc, cat?.CATEGORY],
                []
            );
            searchParams.set(
                "purchasing_category",
                purchasingCategoryIds.join(SEARCH_API_DELIMITER)
            );
            const categoryIds = value.reduce(
                (acc, cat) => [...acc, cat?.CATEGORY_ID],
                []
            );
            searchParams.set(
                "category",
                categoryIds.join(SEARCH_API_DELIMITER)
            );
        } else {
            searchParams.delete("category");
            searchParams.delete("purchasing_category");
        }
        setSearchParams(searchParams);
    };

    const onUnitMeasureSelect = (_e, value) => {
        console.log(value);
        /* 
        Arguments
        ---------
        event : obj
        value : array

        */
        if (value.length > 0) {
            const uomIds = value.reduce(
                (acc, uom) => [...acc, uom?.UNIT_MEAS_LOOKUP_CODE],
                []
            );
            searchParams.set("unit_measure", uomIds.join(SEARCH_API_DELIMITER));
        } else {
            searchParams.delete("unit_measure");
        }
        setSearchParams(searchParams);
    };

    const onCurrencySelect = (_e, value) => {
        console.log(value[0]);
        /* 
        Arguments
        ---------
        event : obj
        value : array
        */
        value.length > 0
            ? searchParams.set(
                  "currency_code",
                  value.join(SEARCH_API_DELIMITER)
              )
            : searchParams.delete("currency_code");
        setSearchParams(searchParams);
    };

    const sortedCurrencies = [...filterLookups.CURRENCIES].sort((a, b) => {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
    });

    const onRadioButtonChange = (_e, value) => {
        setSelectedRadioButtons(value);
        onCheckSmartForms(value, searchParams, setSearchParams);
    };

    const filterPanelProps = { searchParams, setSearchParams };
    return (
        <Grid container item spacing={3}>
            <Grid item xs={12}>
                <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <div></div>
                    <Typography variant="h4" align="center" fontWeight="normal">
                        Filters
                    </Typography>
                    <Tooltip title="Clear search query and filters">
                        <IconButton
                            onClick={clearSearch}
                            aria-label="clearFilters"
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
                {readSearchBarQuery && (
                    <Tooltip
                        arrow
                        placement="top-start"
                        title="Search query entered into search bar"
                    >
                        <Chip
                            label={readSearchBarQuery}
                            variant="outlined"
                            onDelete={() =>
                                clearSearchBarQuery(
                                    "search_query",
                                    searchParams,
                                    setSearchParams
                                )
                            }
                            sx={{ m: "4px 0px" }}
                        />
                    </Tooltip>
                )}
                {readItemNumberQuery && isSmartForms && (
                    <Tooltip
                        arrow
                        placement="top-start"
                        title="Item number query entered into search bar"
                    >
                        <Chip
                            label={"Item Number: " + readItemNumberQuery}
                            variant="outlined"
                            onDelete={() =>
                                clearSearchBarQuery(
                                    "item_number",
                                    searchParams,
                                    setSearchParams
                                )
                            }
                            sx={{ m: "4px 0px" }}
                        />
                    </Tooltip>
                )}
                {readSupplierNumberQuery && isSmartForms && (
                    <Tooltip
                        arrow
                        placement="top-start"
                        title="Supplier number query entered into search bar"
                    >
                        <Chip
                            label={
                                "Supplier Number: " + readSupplierNumberQuery
                            }
                            variant="outlined"
                            onDelete={() =>
                                clearSearchBarQuery("supplier_number")
                            }
                            sx={{ m: "4px 0px" }}
                        />
                    </Tooltip>
                )}
                <GradientDivider />
            </Grid>
            <Grid item xs={12} container justifyContent="space-between">
                <FormControl sx={{ mt: "-16px" }}>
                    <RadioGroup
                        value={selectedRadioButtons}
                        onChange={onRadioButtonChange}
                    >
                        <FormControlLabel
                            value={BOTH_PRODUCTS_SMARTFORMS}
                            control={<Radio color="secondary" />}
                            label="Both"
                        />
                        <FormControlLabel
                            value={CATALOGUE_ITEM}
                            control={<Radio color="secondary" />}
                            label="Products"
                        />
                        <FormControlLabel
                            value={SMART_FORM}
                            control={<Radio color="secondary" />}
                            label="Smart Forms"
                        />
                    </RadioGroup>
                </FormControl>
            </Grid>
            {isSmartForms || isProducts ? (
                <>
                    <Grid item xs={12}>
                        <FilterPanelAutoComplete
                            options={categories}
                            onChange={(_e, v) =>
                                onCategorySelect(
                                    v,
                                    searchParams,
                                    setSearchParams
                                )
                            }
                            checkBoxStyle={checkBoxStyle}
                            placeholder="Type to Search Categories..."
                            label="Category"
                            value={category}
                            controlled={true}
                            displayValue="CATEGORY_DISPLAY_NAME"
                            limit="50"
                            equalityTest={(option, value) =>
                                option.CATEGORY_ID == value.CATEGORY_ID
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FilterPanelAutoComplete
                            options={filterLookups.SUPPLIER_NAME}
                            onChange={(_e, v) =>
                                onSupplierSelect(
                                    v,
                                    searchParams,
                                    setSearchParams
                                )
                            }
                            checkBoxStyle={checkBoxStyle}
                            placeholder="Select Supplier..."
                            label="Supplier"
                            value={suppliers}
                            controlled={true}
                            limit="50"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FilterPanelAutoComplete
                            options={filterLookups.UNIT_OF_MEASURES}
                            onChange={onUnitMeasureSelect}
                            checkBoxStyle={checkBoxStyle}
                            placeholder="Select Unit of Measure..."
                            label="Unit of Measure"
                            value={unitOfMeasures}
                            controlled={true}
                            displayValue="UNIT_OF_MEASURE"
                            limit="50"
                            equalityTest={(option, value) =>
                                option.UNIT_MEAS_LOOKUP_CODE ==
                                value.UNIT_MEAS_LOOKUP_CODE
                            }
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FilterPanelAutoComplete
                            options={sortedCurrencies}
                            onChange={onCurrencySelect}
                            checkBoxStyle={checkBoxStyle}
                            placeholder="Select Currency..."
                            label="Currency"
                            value={currencies}
                            controlled={true}
                        />
                    </Grid>
                </>
            ) : null}
            {isProducts ? <FilterPanel {...filterPanelProps} /> : null}
        </Grid>
    );
};

ProductsFilterPanelDispatcher.propTypes = {
    isSmartForms: PropTypes.bool,
    isProducts: PropTypes.bool,
    searchParams: PropTypes.object,
    setSearchParams: PropTypes.func,
};

export default ProductsFilterPanelDispatcher;
